import { Handle, Position } from '@xyflow/react'
import { Code2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { GraphNodeData } from '@/types/graph'
import { useCanvasStore } from '@store/canvas'

interface CodeNodeProps {
  data: GraphNodeData
  selected?: boolean
  id: string
}

export default function CodeNode({ data, selected, id }: CodeNodeProps) {
  const { t } = useTranslation()
  const removeNode = useCanvasStore((state) => state.removeNode)

  const code = typeof data.config?.code === 'string' ? data.config.code : ''
  const language = typeof data.config?.language === 'string' ? data.config.language : 'javascript'
  const preview = code.length > 120 ? code.slice(0, 120) + '…' : code

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeNode(id)
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border-2 bg-card min-w-48 transition-all ${
        selected
          ? 'border-warning ring-2 ring-warning/30'
          : 'border-warning/30 hover:border-warning'
      }`}
    >
      <Handle type="target" position={Position.Top} />

      {/* Header */}
      <div className="flex items-center justify-between mb-2 group">
        <div className="flex items-center gap-2 flex-1">
          <Code2 size={16} className="text-warning flex-shrink-0" />
          <div className="font-bold text-sm text-foreground truncate">
            {data.label || 'Code'}
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-error-soft rounded text-error"
          title={t('build.deleteNode')}
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="text-xs text-muted-foreground mb-2 bg-card rounded p-2 max-h-20 overflow-y-auto">
        {preview ? (
          <pre className="whitespace-pre-wrap font-mono text-[11px] text-foreground leading-relaxed">
            {preview}
          </pre>
        ) : (
          <div className="text-muted-foreground">{t('build.noCodeConfigured', 'Aucun code configuré')}</div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 text-xs">
        <span className="px-2 py-1 bg-warning-soft text-warning rounded">
          {language}
        </span>
        {data.config?.output_key && (
          <span className="px-2 py-1 bg-muted text-foreground rounded truncate">
            → {String(data.config.output_key)}
          </span>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} />
    </div>
  )
}
